
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { get, post, del, put } from '@/services/http';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { EventBus } from '@/main';
import { PurchaseOrderModel } from '@/models/purchase_order';
import type { IPoPaymentRecord } from '@/models/purchase_order';
import POPaymentStatusBadge from '@/components/purchase-order/POPaymentStatusBadge.vue';
import PORecordPaymentDialog from '@/components/purchase-order/PORecordPaymentDialog.vue';

/**
 * Story 20: payment & invoice section for the PO detail page. Self-contained so
 * the host view only needs to render <POPaymentSection> and react to @updated.
 */
@Component({ components: { POPaymentStatusBadge, PORecordPaymentDialog } })
export default class POPaymentSection extends Vue {
  @Prop({ type: Object, required: true }) readonly purchaseOrder!: PurchaseOrderModel;
  @Prop({ type: Boolean, default: false }) readonly isForeignCurrency!: boolean;

  private payments: IPoPaymentRecord[] = [];
  private loading = false;
  private showRecordDialog = false;
  private markingPaid = false;
  private deletingId: number | null = null;

  // Story 21: invoice fields (local editable copies)
  private invoiceNumber: string | null = null;
  private invoiceDate: string | null = null;
  private paymentDueDate: string | null = null;
  private invoiceDateMenu = false;
  private dueDateMenu = false;
  private savingInvoice = false;

  private readonly methodLabels: Record<string, string> = {
    bank_transfer: 'Bank transfer',
    credit_card: 'Credit card',
    check: 'Check',
    cash: 'Cash',
    ach: 'ACH',
    wire: 'Wire',
    paypal: 'PayPal',
    other: 'Other',
    manual: 'Manual',
  };

  @Watch('purchaseOrder.id', { immediate: true })
  onPoChange(): void {
    this.initInvoiceFields();
    this.fetchPayments();
  }

  private get poInvoiceDate(): string | null {
    // Prefer the canonical key; fall back to the legacy key the Stocky importer
    // wrote (extra.invoiceDate) until Story 23's backfill normalizes it.
    return (this.purchaseOrder.extra?.financial?.invoice_date as string)
      ?? (this.purchaseOrder.extra?.invoiceDate as string)
      ?? null;
  }

  private initInvoiceFields(): void {
    this.invoiceNumber = this.purchaseOrder.invoiceNumber ?? null;
    this.invoiceDate = this.poInvoiceDate;
    this.paymentDueDate = this.purchaseOrder.paymentDueDate ?? null;
  }

  get isInvoiceDirty(): boolean {
    return (this.invoiceNumber || null) !== (this.purchaseOrder.invoiceNumber ?? null)
      || (this.invoiceDate || null) !== (this.poInvoiceDate || null)
      || (this.paymentDueDate || null) !== (this.purchaseOrder.paymentDueDate ?? null);
  }

  private async saveInvoice(): Promise<void> {
    this.savingInvoice = true;
    try {
      await put(`/purchase_orders/${this.purchaseOrder.id}`, {
        invoiceNumber: this.invoiceNumber || null,
        invoiceDate: this.invoiceDate || null,
        paymentDueDate: this.paymentDueDate || null,
      });
      EventBus.$emit('show-snackbar', { message: 'Invoice details saved', color: 'success' });
      this.$emit('updated');
    } catch (error: any) {
      EventBus.$emit('show-snackbar', { message: error?.response?.data?.message || 'Failed to save invoice details', color: 'error' });
    } finally {
      this.savingInvoice = false;
    }
  }

  get currencyCode(): string {
    return this.purchaseOrder.currencyCode || 'USD';
  }

  get grandTotal(): number {
    return this.purchaseOrder.paymentGrandTotal;
  }

  get paidAmount(): number {
    return this.purchaseOrder.paidAmount;
  }

  get outstandingAmount(): number {
    return this.purchaseOrder.outstandingAmount;
  }

  formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.currencyCode);
  }

  methodLabel(method: string | null | undefined): string {
    return method ? (this.methodLabels[method] || method) : '—';
  }

  private async fetchPayments(): Promise<void> {
    if (!this.purchaseOrder?.id) return;
    this.loading = true;
    try {
      const res: any = await get(`/purchase_orders/${this.purchaseOrder.id}/payments`);
      this.payments = (res?.data ?? res) || [];
    } catch (error: any) {
      EventBus.$emit('show-snackbar', { message: error?.response?.data?.message || 'Failed to load payments', color: 'error' });
    } finally {
      this.loading = false;
    }
  }

  private onChanged(): void {
    this.fetchPayments();
    this.$emit('updated');
  }

  private async markPaid(): Promise<void> {
    this.markingPaid = true;
    try {
      await post(`/purchase_orders/${this.purchaseOrder.id}/mark-paid`, {});
      EventBus.$emit('show-snackbar', { message: 'Purchase order marked as paid', color: 'success' });
      this.onChanged();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', { message: error?.response?.data?.message || 'Failed to mark as paid', color: 'error' });
    } finally {
      this.markingPaid = false;
    }
  }

  private async remove(payment: IPoPaymentRecord): Promise<void> {
    this.deletingId = payment.id;
    try {
      await del(`/purchase_orders/${this.purchaseOrder.id}/payments/${payment.id}`);
      EventBus.$emit('show-snackbar', { message: 'Payment deleted', color: 'success' });
      this.onChanged();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', { message: error?.response?.data?.message || 'Failed to delete payment', color: 'error' });
    } finally {
      this.deletingId = null;
    }
  }
}
